import React, { useCallback, useEffect, useState } from 'react';

import {
  DisputeEvidence,
  DisputeQueueRow,
  DisputeStatus,
  ProtectedDisputeEvidence,
  SlaStatus,
  escalateDispute,
  getDisputeEvidence,
  getDisputeQueue,
  resolveDispute,
  retrieveDisputeEvidence,
} from '../api/disputes';
import {
  Drawer,
  Empty,
  Field,
  FilterBar,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  Table,
  Value,
} from '../../../design-system/ops';

/**
 * PI-2 Epic 6 — the moderator's dispute queue and its evidence bundle.
 *
 * Rewritten onto the design system, and restructured while it was open. It was
 * two columns side by side, a list and a detail panel, each with its own
 * borders and its own five SLA colours. It is now the console's own shape: a
 * table you scan and a drawer you work in.
 *
 * The two decisions that move money were taken behind `window.prompt`. A
 * browser prompt cannot be styled, cannot be cancelled cleanly, and gives an
 * operator a bare box with no sight of the evidence they are deciding on —
 * they are typing a resolution into a modal that has covered the recording
 * they were watching. Both now happen in the drawer, beside what they are
 * about.
 *
 * The drawer opens on metadata only. A recording, a rubric or a teacher's notes
 * are minor-sensitive (DEC-A01-06), so they are retrieved on request, with a
 * reason, and held only while this dispute stays open in the drawer.
 */

const STATUS_FILTERS: Array<{ value: 'OPEN' | 'ESCALATED' | 'all'; label: string }> = [
  { value: 'OPEN', label: 'Abiertas' },
  { value: 'ESCALATED', label: 'Escaladas' },
  { value: 'all', label: 'Todas' },
];

const SLA_TONE: Record<SlaStatus, PillTone> = {
  BREACHED: 'bad',
  AT_RISK: 'warn',
  ON_TRACK: 'ok',
  ESCALATED: 'tint',
  RESOLVED: 'neu',
};

const SLA_LABEL: Record<SlaStatus, string> = {
  BREACHED: 'SLA vencido',
  AT_RISK: 'En riesgo',
  ON_TRACK: 'En tiempo',
  ESCALATED: 'Escalada',
  RESOLVED: 'Resuelta',
};

const fmt = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const AdminDisputes: React.FC = () => {
  const [filter, setFilter] = useState<'OPEN' | 'ESCALATED' | 'all'>('OPEN');
  const [queue, setQueue] = useState<DisputeQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<DisputeEvidence | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  /** The written reason, shared by escalation and resolution. */
  const [reason, setReason] = useState('');

  /** Separate from the resolution: this one goes in the audit log. */
  const [accessReason, setAccessReason] = useState('');
  const [restricted, setRestricted] = useState<ProtectedDisputeEvidence | null>(null);
  const [retrieving, setRetrieving] = useState(false);
  const [restrictedError, setRestrictedError] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDisputeQueue(filter === 'all' ? undefined : (filter as DisputeStatus));
      setQueue(res.disputes);
    } catch (e: any) {
      setError(e?.message || 'Error cargando la cola de disputas');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const openEvidence = async (disputeId: string) => {
    setError(null);
    setInfo(null);
    setReason('');
    if (selected?.dispute.disputeId !== disputeId) {
      setRestricted(null);
      setAccessReason('');
      setRestrictedError(null);
    }
    try {
      setSelected(await getDisputeEvidence(disputeId));
    } catch (e: any) {
      setError(e?.message || 'Error cargando la evidencia');
    }
  };

  const closeEvidence = () => {
    setSelected(null);
    setRestricted(null);
    setAccessReason('');
    setRestrictedError(null);
  };

  const retrieveRestricted = async (disputeId: string) => {
    setRetrieving(true);
    setRestrictedError(null);
    try {
      setRestricted(await retrieveDisputeEvidence(disputeId, accessReason.trim()));
    } catch (e: any) {
      // The refusal stays beside the request: the rest of the drawer still
      // works, and an operator without this permission can still escalate.
      setRestrictedError(e?.message || 'No se pudo abrir la evidencia protegida');
    } finally {
      setRetrieving(false);
    }
  };

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await fn();
      setInfo(ok);
      setReason('');
      await loadQueue();
      if (selected) await openEvidence(selected.dispute.disputeId);
    } catch (e: any) {
      setError(e?.message || 'No se pudo completar la acción');
    } finally {
      setBusy(false);
    }
  };

  const onEscalate = (d: DisputeEvidence) =>
    run(
      () => escalateDispute(d.dispute.disputeId, reason.trim() || undefined),
      'Disputa escalada a revisión humana.',
    );

  const onResolve = (d: DisputeEvidence, status: 'RESOLVED_RELEASED' | 'RESOLVED_REFUNDED') =>
    run(
      () => resolveDispute(d.dispute.enrollmentId, d.dispute.disputeId, status, reason.trim()),
      status === 'RESOLVED_RELEASED'
        ? 'Resuelta: liberado al profesor.'
        : 'Resuelta: reembolsado a la familia.',
    );

  const unresolved =
    selected?.dispute.status === 'OPEN' || selected?.dispute.status === 'ESCALATED';
  const milestone = selected?.milestone;
  const available = selected?.available;
  const anyRestricted =
    available &&
    (available.rubric || available.deliverable || available.recording || available.teacherNotes);

  return (
    <OpsPage
      title="Disputas"
      subtitle="Mientras una disputa sigue abierta el dinero del hito está congelado y ninguna parte puede avanzar."
    >
      <FilterBar
        label="Filtrar por estado"
        value={filter}
        onChange={setFilter}
        options={STATUS_FILTERS}
      />

      {error ? <Note tone="bad">{error}</Note> : null}
      {info ? <Note tone="warn">{info}</Note> : null}

      {loading ? (
        <Note>Cargando…</Note>
      ) : queue.length === 0 ? (
        <Empty
          title="Sin disputas"
          detail={filter === 'all' ? 'No hay ninguna registrada.' : 'Ninguna en ese estado.'}
        />
      ) : (
        <Table columns="1fr 200px 150px 130px" head={['Curso', 'Partes', 'Abierta', 'SLA']}>
          {queue.map((d) => (
            <Row
              key={d.disputeId}
              onOpen={() => openEvidence(d.disputeId)}
              label={`Ver la disputa ${d.disputeId}`}
            >
              <span>
                <b>{d.courseTitle || d.courseId}</b>
                <br />
                <span className="mut">Sesión {d.milestoneSequence ?? '?'}</span>
              </span>
              <span className="mut">
                {d.parent.name || 'Familia'} vs {d.teacher.name || 'Profesor'}
              </span>
              <span className="mut">
                {fmt(d.openedAt)}
                {d.sla.slaDueAt ? (
                  <>
                    <br />
                    vence {fmt(d.sla.slaDueAt)}
                  </>
                ) : null}
              </span>
              <Pill tone={SLA_TONE[d.sla.slaStatus]}>{SLA_LABEL[d.sla.slaStatus]}</Pill>
            </Row>
          ))}
        </Table>
      )}

      <Drawer
        open={selected !== null}
        onClose={closeEvidence}
        title="Evidencia"
        meta={selected ? `${selected.dispute.disputeId} · ${selected.dispute.status}` : undefined}
        footer={
          selected && unresolved ? (
            <>
              {selected.dispute.status === 'OPEN' ? (
                <button className="btn btn-o" disabled={busy} onClick={() => onEscalate(selected)}>
                  Escalar
                </button>
              ) : null}
              <button
                className="btn btn-o"
                disabled={busy || !reason.trim()}
                onClick={() => onResolve(selected, 'RESOLVED_REFUNDED')}
              >
                Reembolsar a la familia
              </button>
              <button
                className="btn btn-p"
                disabled={busy || !reason.trim()}
                onClick={() => onResolve(selected, 'RESOLVED_RELEASED')}
              >
                Liberar al profesor
              </button>
            </>
          ) : null
        }
      >
        {selected && milestone ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <Pill tone={SLA_TONE[selected.dispute.sla.slaStatus]}>
                {SLA_LABEL[selected.dispute.sla.slaStatus]}
              </Pill>
            </div>

            <Field label="Motivo de la familia">
              <Note tone="warn">{selected.dispute.reason}</Note>
            </Field>

            {selected.dispute.escalationNote ? (
              <Field label="Nota de escalación">
                <Note>{selected.dispute.escalationNote}</Note>
              </Field>
            ) : null}

            <Field label={`Sesión ${milestone.sequence}`}>
              <Value>{milestone.sessionState || milestone.status}</Value>
            </Field>

            <Field label="Entrada a la videollamada">
              <Value>
                Profesor {fmt(milestone.teacherCheckedInAt)} · Alumno{' '}
                {fmt(milestone.studentCheckedInAt)} · {milestone.durationMinutes ?? '—'} min
              </Value>
            </Field>

            <Field label="Evidencia disponible">
              <Value>
                {[
                  available?.recording ? 'Grabación' : null,
                  available?.deliverable ? 'Entregable' : null,
                  available?.rubric ? 'Rúbrica' : null,
                  available?.teacherNotes ? 'Notas del profesor' : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Sin evidencia registrada'}
              </Value>
            </Field>

            {anyRestricted && !restricted ? (
              <Field label="Evidencia protegida">
                <textarea
                  id={`dispute-access-${selected.dispute.disputeId}`}
                  className="inp"
                  rows={2}
                  value={accessReason}
                  aria-label="Motivo de acceso a la evidencia protegida"
                  placeholder="Por qué necesitas verla. Queda en el registro de auditoría."
                  onChange={(e) => setAccessReason(e.target.value)}
                />
                {restrictedError ? <Note tone="bad">{restrictedError}</Note> : null}
                <button
                  className="btn btn-o"
                  disabled={retrieving || !accessReason.trim()}
                  onClick={() => retrieveRestricted(selected.dispute.disputeId)}
                >
                  {retrieving ? 'Abriendo…' : 'Abrir evidencia protegida'}
                </button>
              </Field>
            ) : null}

            {restricted ? (
              <>
                <Field label="Alumno">
                  <Value>{restricted.childFirstName || 'No disponible'}</Value>
                </Field>

                <Field label="Rúbrica">
                  <Value>
                    {restricted.rubric
                      ? `${restricted.rubric.overall}/5 — ${restricted.rubric.summary}`
                      : 'Sin rúbrica'}
                  </Value>
                </Field>

                <Field label="Notas del profesor">
                  <Value>{restricted.teacherNotes || 'Sin notas'}</Value>
                </Field>

                <Field label="Entregable">
                  <Value>
                    {restricted.deliverable?.signedUrl ? (
                      <a href={restricted.deliverable.signedUrl} target="_blank" rel="noreferrer">
                        Abrir entregable
                      </a>
                    ) : restricted.deliverable ? (
                      'No se pudo generar el enlace'
                    ) : (
                      'Sin entregable'
                    )}
                  </Value>
                </Field>

                <Field label="Grabación">
                  {restricted.recording?.signedUrl ? (
                    <video
                      controls
                      aria-label="Grabación de la sesión"
                      src={restricted.recording.signedUrl}
                      style={{ width: '100%', maxHeight: 320, borderRadius: 8 }}
                    />
                  ) : (
                    <Value>
                      {restricted.recording ? 'No se pudo generar el enlace' : 'Sin grabación'}
                    </Value>
                  )}
                </Field>
              </>
            ) : null}

            {unresolved ? (
              <Field label="Resolución">
                {/* Beside the evidence rather than in a browser prompt that
                    covers it. A resolution is written while looking at the
                    recording it is about. */}
                <textarea
                  id={`dispute-reason-${selected.dispute.disputeId}`}
                  className="inp"
                  rows={3}
                  value={reason}
                  aria-label="Resolución o motivo"
                  placeholder="Qué decidiste y por qué. La familia y el profesor lo verán."
                  onChange={(e) => setReason(e.target.value)}
                />
                {!reason.trim() ? (
                  <Note>
                    Liberar o reembolsar necesita una resolución escrita. Escalar no la necesita,
                    aunque ayuda a quien la reciba.
                  </Note>
                ) : null}
              </Field>
            ) : null}
          </>
        ) : null}
      </Drawer>
    </OpsPage>
  );
};

export default AdminDisputes;
